import { useEffect, useMemo, useState } from "react";

import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import LightbulbRoundedIcon from "@mui/icons-material/LightbulbRounded";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import { Box } from "@mui/material";
import Button from "@mui/material/Button";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";

import { IPSearchAnalyticsService, UserAnalyticsService } from "@analytics/index";

import CompanyDetailsDrawer from "@components/CompanyDetailsDrawer/CompanyDetailsDrawer";
import CompanyDisplay from "@components/CompanyDetailsDrawer/CompanyDisplay";
import CompanyFilterDrawer from "@components/FilterControls/CompanyFilterDrawer";
import {
  TableToolbar,
  TableToolbarDropdown,
  TableToolbarDropdownMenuItem,
  TableToolbarFilterButton,
  TableToolbarLeft,
  TableToolbarRight,
  TableToolbarSearch,
} from "@components/TableToolbar/TableToolbar";

import useHandleCompanySelection from "@hooks/useHandleCompanySelection";

import { GetChatRequestType } from "@interfaces/chat.type";
import { CompanyType } from "@interfaces/company.type";
import { IFetchCompanySearch } from "@interfaces/ipsearch.type";

import CompaniesTable from "@pages/Companies/components/CompaniesTable";
import useEmbeddingSearchToolbar from "@pages/Companies/components/useEmbeddingSearchToolbar";
import useEmbeddingTableConfig from "@pages/Companies/components/useEmbeddingTableConfig";

import { useFetchCompanyInsight, useLoadCompanySearch } from "@queries/ipSearch.query";

import CompanyService from "@services/company.service";

import { useTableStore } from "@store/filters/filters.store";
import { INITIAL_FILTERS_STATES_MAP } from "@store/filters/initialStates";
import { useOrganizationStore } from "@store/organizationStore";
import useSmartSearchStore from "@store/smartSearch";
import { useUserStore } from "@store/userStore";

import contentCardStyles from "@theme/contentCardStyles";

import { handleFuzzySort } from "@utilities/handleFuzzySort";
import numberOfActiveFilters from "@utilities/numberOfActiveFilters";

interface IIpSearchCompaniesProperties {
  fetchCompanySearch: ({ preferredIndustries, queryString }: IFetchCompanySearch) => void;
  // TODO: Can we move this into the companies table store as ui state? Then we just control if modal is open or not. Then we can get rid of setIsSmartSearchFromTechnologyOverview
  shouldTriggerModal: boolean;
}

export default function EmbeddingSearch({ fetchCompanySearch, shouldTriggerModal }: IIpSearchCompaniesProperties) {
  const organization = useOrganizationStore((state) => state.organization);

  const modelUsed = (organization?.chatModel || "openai") as GetChatRequestType["model"];

  const { filters, pagination, selection, setTableFilters, setTablePagination, setTableSort, sort } =
    useTableStore("company");

  const [search, setSearch] = useState<{ companies: CompanyType[]; queryString: string }>({
    companies: [],
    queryString: "",
  });
  const { isSmartSearchEnabled, setIsSmartSearchFromTechnologyOverview } = useSmartSearchStore();

  const handleSetSelectedCompanyIds = useHandleCompanySelection();

  const [focusedId, setFocusedId] = useState<null | string>(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const user = useUserStore();

  const { data: currentSearch, status: searchStatus } = useLoadCompanySearch(user.currentSearch ?? "new");

  const handleSetFocusedId = (id: string) => {
    setFocusedId(id);
  };

  useEffect(() => {
    if (searchStatus === "success" && currentSearch) {
      setSearch({
        ...currentSearch,
        queryString: currentSearch.queryString ?? "",
      });
    }
  }, [currentSearch, searchStatus]);

  const { handleAddCompaniesToTechnology, handleSmartSearch, toggleFilterVisibility } = useEmbeddingSearchToolbar({
    allCompanies: currentSearch?.companies ?? [],
    fetchCompanySearch,
    isFilterOpen,
    searchId: user.currentSearch ?? "new",
    selectedIds: selection,
    setIsFilterOpen,
  });

  const { data: insightData, mutate: fetchInsight, status: insightStatus } = useFetchCompanyInsight();

  const isInsightLoading = insightStatus === "pending";

  useEffect(() => {
    if (insightData) {
      setSearch({
        ...insightData,
        queryString: insightData.queryString ?? "",
      });
      IPSearchAnalyticsService.fetchAIInsights();
    }
  }, [insightData]);

  const filteredCompanies = useMemo(() => {
    if (filters.search && filters.search.trim().length > 0) {
      const filteredCompanies = CompanyService.filterCompanies(currentSearch?.companies ?? [], {
        filters,
        pagination,
        selection,
        sort,
      });

      return handleFuzzySort(filters.search, filteredCompanies, "name");
    }
    return CompanyService.filterCompanies(currentSearch?.companies ?? [], {
      filters,
      pagination,
      selection,
      sort,
    });
  }, [currentSearch?.companies, filters]);

  const fetchCompaniesInsights = (companyId: string) => {
    let messages: string[] | undefined;
    if (focusedCompany?.insight) {
      messages = Array.isArray(focusedCompany.insight) ? focusedCompany.insight : [focusedCompany.insight];
    }
    fetchInsight({
      companyId,
      messages,
      model: modelUsed,
      searchId: user.currentSearch ?? "new",
    });
  };

  const { columns, handleRowFocus, initialState, rows } = useEmbeddingTableConfig({
    companies: filteredCompanies,
    setFocusedId: handleSetFocusedId,
    smartSearch: currentSearch,
  });

  const focusedCompany = useMemo(() => {
    if (focusedId) {
      return search.companies.find((company) => company.id === focusedId);
    }
    return null;
  }, [focusedId, search.companies]);

  if (shouldTriggerModal) {
    handleSmartSearch();
    setIsSmartSearchFromTechnologyOverview(false);
  }

  return (
    <Box sx={contentCardStyles}>
      <CompanyDetailsDrawer close={() => setFocusedId(null)} isOpen={focusedId !== null}>
        {focusedCompany && (
          <CompanyDisplay
            close={() => setFocusedId(null)}
            company={focusedCompany}
            getInsight={fetchCompaniesInsights}
            isInsightLoading={isInsightLoading}
            isSmartSearch={isSmartSearchEnabled}
          />
        )}
      </CompanyDetailsDrawer>
      <CompanyFilterDrawer isOpen={isFilterOpen} setOpen={setIsFilterOpen} />
      <>
        <TableToolbar>
          <TableToolbarLeft>
            <Button
              color={isSmartSearchEnabled ? "primary" : "secondary"}
              onClick={handleSmartSearch}
              startIcon={<ManageSearchIcon />}
              sx={{ borderRadius: "8px" }}
              variant="contained"
            >
              Portfolio Search
            </Button>
            <TableToolbarSearch
              placeholder="Search Company Name"
              query={filters.search ?? ""}
              setQuery={(query) => {
                UserAnalyticsService.trackCompanySearchBarUsed(query);
                setTableFilters({ search: query });
              }}
            />
          </TableToolbarLeft>
          <TableToolbarRight>
            <TableToolbarFilterButton
              activeFilters={numberOfActiveFilters(filters, INITIAL_FILTERS_STATES_MAP.company["filters"])}
              toggleFilterVisibility={toggleFilterVisibility}
              tooltipText="Filter companies based on location, industry, and more."
            />
            <TableToolbarDropdown
              actionsLabel="Save Company"
              selectedIds={selection}
              tooltipText="Save selected companies to your Portfolio or download to CSV."
            >
              <TableToolbarDropdownMenuItem onClick={handleAddCompaniesToTechnology}>
                <ListItemIcon>
                  <LightbulbRoundedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Save To Portfolio</ListItemText>
              </TableToolbarDropdownMenuItem>
              <TableToolbarDropdownMenuItem onClick={() => CompanyService.exportCompanies(selection, search.companies)}>
                <ListItemIcon>
                  <DownloadRoundedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Export Company Data</ListItemText>
              </TableToolbarDropdownMenuItem>
            </TableToolbarDropdown>
          </TableToolbarRight>
        </TableToolbar>
        <CompaniesTable
          columns={columns}
          handlePaginationModelChange={setTablePagination}
          handleRowFocus={handleRowFocus}
          handleSortModelChange={setTableSort}
          initialState={initialState}
          isCompanySearching={searchStatus === "pending"}
          noRowsLabel="No results found. Try disabling your Smart Search to find the company you are looking for."
          pagination={pagination}
          paginationMode="client"
          rows={rows}
          selection={selection}
          setSelection={handleSetSelectedCompanyIds}
          sortingMode="client"
          sortModel={sort}
        />
      </>
    </Box>
  );
}
